import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const response = await axios.post(`${API_URL}/api/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        // Save token and user
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast({
          title: 'Welcome back!',
          description: `Logged in as ${response.data.user.name}`,
        });

        // Redirect to home
        navigate('/');
        
      } else {
        // Register
        const response = await axios.post(`${API_URL}/api/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name
        });

        // Save token and user
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast({
          title: 'Account created!',
          description: 'Welcome to Agora Marketplace',
        });

        // Redirect to home
        navigate('/');
      }

    } catch (error) {
      console.error('Auth error:', error);
      
      // Handle different error formats
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.response?.data) {
        const detail = error.response.data.detail;
        
        // Pydantic validation errors (array format)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            // Extract field name and message
            const field = err.loc ? err.loc[err.loc.length - 1] : '';
            const msg = err.msg || '';
            return field ? `${field}: ${msg}` : msg;
          }).join('. ');
        } 
        // String error message
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Object with msg property
        else if (detail && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      // Provide helpful hints for common validation errors
      if (errorMessage.includes('email') || error.response?.status === 422) {
        errorMessage += '\n\nPlease use a valid email format (e.g., user@example.com)';
      }
      
      toast({
        title: isLogin ? 'Login failed' : 'Registration failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-500 rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">A</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isLogin ? 'Login to your account' : 'Sign up to start selling'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                required
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@example.com"
              required
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              title="Please enter a valid email address"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Use a valid email format (e.g., user@example.com)</p>
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              required
              minLength={6}
              className="mt-1"
            />
            {!isLogin && (
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-red-500 hover:bg-red-600 text-white py-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isLogin ? 'Logging in...' : 'Creating account...'}
              </>
            ) : (
              <>{isLogin ? 'Login' : 'Create Account'}</>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-red-500 hover:text-red-600 font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
          </button>
        </div>

        <div className="mt-6">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 block text-center">
            ← Back to home
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
